/**
 * Hintergrundjobs (tri-server backgroundJobs.ts) — lange Werkzeugläufe wie das Leistungsverzeichnis
 * aus einer hochgeladenen Anlagenliste. Die Chat-Karte (`JobResource`) fragt den Stand ab; der
 * Wächter in der Shell meldet per Toast, wenn ein Job fertig wird, während man woanders ist.
 */

import { atom, useAtom } from "jotai";
import { useEffect } from "react";
import { toast } from "sonner";

import { configuration } from "./Configuration";
import { ApplicationError, UnauthorizedError } from "./general/Errors";
import { authenticatedFetch } from "./JWTAuthService";

export type BackgroundJobStatus = "queued" | "running" | "done" | "failed" | "cancelled";

export interface BackgroundJob {
  _id: string;
  kind: string;
  title: string;
  conversationId?: string | null;
  status: BackgroundJobStatus;
  progress: Array<{ at: string; text: string }>;
  counter?: { label: string; done: number; total: number; at: string; since: string; doneAtSince: number } | null;
  result?: { text: string; resources: any[] } | null;
  error?: string | null;
  attempts: number;
  createdAt: string;
  startedAt?: string | null;
  finishedAt?: string | null;
}

export const isActiveJob = (job?: { status?: string } | null) =>
  job?.status === "queued" || job?.status === "running";

const request = async (method: string, path: string) => {
  const response = await authenticatedFetch(`${configuration.serverUrl}${path}`, {
    method,
    headers: { "Content-Type": "application/json" },
  });
  if (response.ok) return response.json();
  if (response.status === 401) throw new UnauthorizedError();
  let message = `${response.status} ${response.statusText}`;
  try {
    const body = await response.json();
    message = body.message || body.error || message;
  } catch {
    // keep the status text
  }
  throw new ApplicationError(message);
};

export const backgroundJobsAPI = {
  getJob: (customer: string, id: string): Promise<BackgroundJob> =>
    request("GET", `/${customer}/background-jobs/${encodeURIComponent(id)}`),
  listJobs: (customer: string, options: { active?: boolean; since?: string } = {}): Promise<BackgroundJob[]> => {
    const query = new URLSearchParams();
    if (options.active) query.set("active", "true");
    if (options.since) query.set("since", options.since);
    return request("GET", `/${customer}/background-jobs${query.toString() ? `?${query}` : ""}`);
  },
  cancel: (customer: string, id: string): Promise<BackgroundJob> =>
    request("POST", `/${customer}/background-jobs/${encodeURIComponent(id)}/cancel`),
};

/** Ids of jobs known to be running — set by the chat card, read by the shell watcher. */
export const activeBackgroundJobsAtom = atom<string[]>([]);

/** Fired when a job ends, so an open chat on that conversation reloads and shows the result. */
export const BACKGROUND_JOB_FINISHED_EVENT = "tri:background-job-finished";

const announcedJobs = new Set<string>();

/** Announces a finished job once per page — true the first time, false after (card or watcher). */
export const announceJobFinished = (job: BackgroundJob): boolean => {
  if (announcedJobs.has(job._id)) return false;
  announcedJobs.add(job._id);
  window.dispatchEvent(
    new CustomEvent(BACKGROUND_JOB_FINISHED_EVENT, { detail: { jobId: job._id, conversationId: job.conversationId } }),
  );
  return true;
};

/**
 * Shell watcher: while jobs run, ask every 15 s; when one ends, a toast with a link into its
 * conversation. Without running jobs it asks once at start (jobs started before a reload) and
 * then only when a chat card reports a new one.
 */
export const useBackgroundJobWatcher = (
  customer: string | undefined,
  texts: { done: (title: string) => string; failed: (title: string) => string; open: string },
  openConversation: (conversationId: string) => void,
) => {
  const [active, setActive] = useAtom(activeBackgroundJobsAtom);

  useEffect(() => {
    if (!customer) return;
    backgroundJobsAPI
      .listJobs(customer, { active: true })
      .then((jobs) => setActive((current) => Array.from(new Set(current.concat(jobs.map((job) => job._id))))))
      .catch(() => undefined);
  }, [customer, setActive]);

  useEffect(() => {
    if (!customer || active.length === 0) return;
    let cancelled = false;
    const check = async () => {
      for (const id of active) {
        try {
          const job = await backgroundJobsAPI.getJob(customer, id);
          if (cancelled || isActiveJob(job)) continue;
          setActive((current) => current.filter((other) => other !== id));
          // The chat card may have seen the end first — then the reader is looking at it already.
          if (!announceJobFinished(job) || job.status === "cancelled") continue;
          const notify = job.status === "done" ? toast.success : toast.error;
          notify(job.status === "done" ? texts.done(job.title) : texts.failed(job.title), {
            duration: 15000,
            ...(job.conversationId
              ? { action: { label: texts.open, onClick: () => openConversation(job.conversationId!) } }
              : {}),
          });
        } catch {
          // a transient failure is retried at the next tick
        }
      }
    };
    const timer = setInterval(check, 15000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, active.join(",")]);
};
